import { ChangeEvent, useEffect, useState } from 'react'
import { InputGroup, InputRightElement, SimpleGrid, Spinner, Stack } from '@chakra-ui/react'

import { MaskedInput, Span } from 'components'
import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import { cpfMask } from 'utils/masks'
import { capitalizeString, removeEverythingThatIsNotNumber } from 'utils/transforms/string'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import { SEXES_OBJECT } from 'constants/variables'
import useGetPersonByCPF from 'components/tools/CustomForm/hooks/useGetPersonByCPF'
import { formatDateToString } from 'utils/transforms/date'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import validateCPFOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateCPFOnCustomForm'
import CustomFormDocumentUploads from '../../components/CustomFormDocumentUploads'
import useCustomFormGetDefaultValue from '../../../../hooks/useCustomFormGetDefaultValue'

export default function CustomFormCPFQuestion({
  question,
  questionKey,
  disabled,
  formMethods,
}: CustomFormViewerQuestionProps) {
  const [cpfNumber, setCpfNumber] = useState<string>('')

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const questionWithError = thisQuestionHasError(question.slug)?.question

  const { getPersonByCPF, loadingCPF, person, setPerson } = useGetPersonByCPF()

  const initialValue = ['', '', '', '', '', '', '', '', '', '', '', '']

  async function completeValidateCPF(cpf: string) {
    const errorMessage = validateCPFOnCustomForm({ cpf, isRequired: question.is_required })
    if (errorMessage === '') {
      const apiErrorMessage = await getPersonByCPF(cpf)
      if (apiErrorMessage === '') {
        removeErrorFromQuestion(question.slug)
      } else {
        setErrorOnQuestion(question, apiErrorMessage)
      }
    } else {
      setPerson({})
      setErrorOnQuestion(question, errorMessage)
    }
  }

  async function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    const cpf = event.target.value
    setCpfNumber(cpf)
  }

  async function handleOnBlur(event: ChangeEvent<HTMLInputElement>) {
    const cpf = event.target.value
    completeValidateCPF(cpf)
  }

  const { defaultValue } = useCustomFormGetDefaultValue({ question, questionKey, formMethods })

  useEffect(() => {
    if (defaultValue) {
      const defaultCPF = defaultValue
      setCpfNumber(defaultCPF)
      completeValidateCPF(defaultCPF)
    }
  }, [defaultValue])

  useEffect(() => {
    if (formMethods?.setValue) {
      if (question?.answer) {
        formMethods?.setValue(questionKey, question.answer)
        setCpfNumber(cpfMask(question.answer[0]))
        setPerson({
          name: question.answer[1],
          mother_name: question.answer[2],
          father_name: question.answer[3],
          birth_certificate_date: question.answer[4],
          sex: question.answer[5],
        })
      } else {
        formMethods?.setValue(questionKey, initialValue)
      }
    }
  }, [question])

  useEffect(() => {
    if (formMethods?.setValue) {
      const currentValues = formMethods?.watch(questionKey) || []
      if (person.name) {
        currentValues[0] = removeEverythingThatIsNotNumber(cpfNumber)
        currentValues[1] = person.name || ''
        currentValues[2] = person.mother_name || ''
        currentValues[3] = person.father_name || ''
        currentValues[4] = person.birth_certificate_date || ''
        currentValues[5] = person.sex || ''
        formMethods?.setValue(questionKey, currentValues)
      } else {
        if (cpfNumber) {
          initialValue[0] = removeEverythingThatIsNotNumber(cpfNumber)
          formMethods?.setValue(questionKey, initialValue)
        } else {
          formMethods?.setValue(questionKey, initialValue)
        }
      }
    }
  }, [person])

  return (
    <Stack>
      <InputGroup>
        <MaskedInput
          id={question.slug}
          value={cpfNumber}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          mask={cpfMask}
          isDisabled={disabled}
          placeholder={question?.placeholder}
        />

        <InputRightElement>{loadingCPF && <Spinner />}</InputRightElement>
      </InputGroup>

      <CustomFormFieldErrorMessage message={questionWithError?.error_message} />

      {person.name && (
        <SimpleGrid columns={[1, 1, 2, 2, 2, 2]} gap={1}>
          <Span label="Nome: " value={capitalizeString(person.name)} />
          <Span label="Nome da mãe: " value={capitalizeString(person.mother_name)} />
          <Span label="Nome do pai: " value={capitalizeString(person.father_name)} />
          <Span label="Data de nascimento: " value={formatDateToString(person.birth_certificate_date, false)} />
          <Span label="Sexo: " value={SEXES_OBJECT[person.sex || 'UNKNOWM']} />
        </SimpleGrid>
      )}

      {question.settings?.document_has_uploads && (
        <CustomFormDocumentUploads
          fileArrayPositions={[6, 7, 8]}
          question={question}
          questionKey={questionKey}
          formMethods={formMethods}
          disabled={disabled}
        />
      )}
    </Stack>
  )
}
